import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { prefersReducedMotion } from './usePrefersReducedMotion';

/**
 * Fades/slides cards in as they enter the viewport (dashboard card grids,
 * lists, marketing sections) — triggers once per element (observer
 * disconnects after the first intersection) rather than re-firing every
 * time the user scrolls back past it. Respects prefers-reduced-motion: the
 * element is simply set to its final visible state with no animation, so
 * reduced-motion users still see the content, just without the motion.
 */
export function useFadeInOnScroll({ y = 20, duration = 0.5, delay = 0 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (prefersReducedMotion()) {
      gsap.set(node, { opacity: 1, y: 0 });
      return undefined;
    }

    gsap.set(node, { opacity: 0, y });

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          gsap.to(node, { opacity: 1, y: 0, duration, delay, ease: 'power2.out' });
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [y, duration, delay]);

  return ref;
}
